import {
  invoiceAttachmentContentType,
  MAX_INVOICE_ATTACHMENT_BYTES,
  MAX_INVOICE_ATTACHMENTS,
} from '@clockwise/shared';
import { formatBytes } from '@/lib/format-bytes';

export interface ScreenedFiles {
  accepted: File[];
  /** One message per refused file, naming it — "which one, and why". */
  rejected: string[];
}

/**
 * Sort dropped or chosen files into those worth uploading and those the
 * server would refuse, BEFORE any bytes leave the browser.
 *
 * The same rules the API enforces (from `@clockwise/shared`), applied early so
 * a 40 MB video or a `.exe` is refused instantly and by name, rather than after
 * an upload. The server stays the authority: it re-checks every one.
 *
 * `existing` is how many the invoice already has, so the per-invoice cap
 * counts the files accepted so far in this same batch.
 */
export function screenFiles(files: readonly File[], existing: number): ScreenedFiles {
  const accepted: File[] = [];
  const rejected: string[] = [];
  for (const file of files) {
    if (!invoiceAttachmentContentType(file.name)) {
      rejected.push(`${file.name}: not a supported file type.`);
    } else if (file.size === 0) {
      rejected.push(`${file.name}: the file is empty.`);
    } else if (file.size > MAX_INVOICE_ATTACHMENT_BYTES) {
      rejected.push(
        `${file.name}: ${formatBytes(file.size)} is over the ${formatBytes(MAX_INVOICE_ATTACHMENT_BYTES)} limit.`,
      );
    } else if (existing + accepted.length >= MAX_INVOICE_ATTACHMENTS) {
      rejected.push(
        `${file.name}: an invoice can have at most ${MAX_INVOICE_ATTACHMENTS} attachments.`,
      );
    } else {
      accepted.push(file);
    }
  }
  return { accepted, rejected };
}
